import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  output,
  OutputEmitterRef,
  Pipe,
  PipeTransform,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { NgClass, NgStyle } from '@angular/common';
import { AppendTestIdPipe } from '../../../../../../pipes/append-test-id/append-test-id.pipe';
import { CorporateDesignService } from '../../../../../../services/corporate-design/corporate-design.service';

@Pipe({
  name: 'IsYearBeforeMin',
  standalone: true,
})
class IsYearBeforeMin implements PipeTransform {
  transform(year: number, minDate: Date | undefined): boolean {
    if (!minDate) {
      return false;
    }
    return year < minDate.getFullYear();
  }
}

@Pipe({
  name: 'IsYearAfterMax',
  standalone: true,
})
class IsYearAfterMax implements PipeTransform {
  transform(year: number, maxDate: Date | undefined): boolean {
    if (!maxDate) {
      return false;
    }
    return year > maxDate.getFullYear();
  }
}

@Pipe({
  name: 'IsYearNotInDateList',
  standalone: true,
})
class IsYearNotInDateList implements PipeTransform {
  transform(year: number, dateList: Date[] | null): boolean {
    if (!dateList) {
      return false;
    }
    return !dateList.some((date: Date) => date.getFullYear() === year);
  }
}

@Component({
  selector: 'aw-year-picker',
  imports: [
    NgClass,
    AppendTestIdPipe,
    NgStyle,
    IsYearBeforeMin,
    IsYearAfterMax,
    IsYearNotInDateList,
  ],
  templateUrl: './year-picker.component.html',
})
export class YearPickerComponent {
  public readonly id: InputSignal<string> = input.required<string>();
  public readonly date: InputSignal<Date | undefined> = input<Date | undefined>(
    undefined
  );
  public readonly shownYear: InputSignal<number> = input.required();
  public readonly pickerPageSize: InputSignal<number> = input(24);
  public readonly focusColor: InputSignal<string> = input.required();
  public readonly borderRounding: InputSignal<string> = input('');
  public readonly min: InputSignal<Date | undefined> = input();
  public readonly max: InputSignal<Date | undefined> = input();
  public readonly restrictedDateList: InputSignal<Date[] | null> = input<
    Date[] | null
  >(null);

  public readonly onYearChange: OutputEmitterRef<number> = output<number>();

  protected readonly corporateDesignService: CorporateDesignService = inject(
    CorporateDesignService
  );

  protected readonly showYearPicker: WritableSignal<boolean> = signal(false);
  protected readonly yearShift: WritableSignal<number> = signal(0);

  protected highlightedYears: number[] = [];

  protected readonly currentYear: Signal<number | undefined> = computed(() => {
    return this.date()?.getFullYear();
  });
  protected readonly years: Signal<number[]> = computed(() => {
    return Array(this.pickerPageSize())
      .fill(0)
      .map(
        (value: number, index: number) =>
          this.shownYear() +
          this.yearShift() * this.pickerPageSize() +
          (index - Math.round(this.pickerPageSize() / 2))
      );
  });

  protected textColor: Signal<string> = computed(() => {
    return this.corporateDesignService.isDarkModeActive()
      ? this.corporateDesignService.corporateDesign().colorAdmixtures.dark_text
      : '';
  });

  protected shiftYearsBack(): void {
    this.yearShift.set(this.yearShift() - 1);
  }

  protected shiftYearsForward(): void {
    this.yearShift.set(this.yearShift() + 1);
  }

  protected removeFromHighlightedYears(year: number): void {
    this.highlightedYears = this.highlightedYears.filter(
      (y: number) => y !== year
    );
  }
  protected selectYear(year: number): void {
    this.onYearChange.emit(year);
    // timeout required so element is still visible for event subscription in date-input
    setTimeout(() => this.showYearPicker.set(false), 200);
  }

  private isYearValid(year: number): boolean {
    const min: Date | undefined = this.min();
    const max: Date | undefined = this.max();
    const listDates: Date[] | null = this.restrictedDateList();

    if (min && year < min.getFullYear()) return false;
    if (max && year > max.getFullYear()) return false;

    if (listDates && listDates.length > 0) {
      const allowedYears = new Set(
        listDates.map((date: Date) => date.getFullYear())
      );
      if (!allowedYears.has(year)) return false;
    }
    return true;
  }

  private getShiftedYears(direction: -1 | 1): number[] {
    const current: number[] = this.years();
    return current.map(
      (year: number) => year + direction * this.pickerPageSize()
    );
  }

  canShiftBack(): boolean {
    return this.getShiftedYears(-1).some((year: number) =>
      this.isYearValid(year)
    );
  }

  canShiftForward(): boolean {
    return this.getShiftedYears(1).some((year: number) =>
      this.isYearValid(year)
    );
  }
}
